import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import * as React from "react";
import { SlidersHorizontal, X, ChevronRight, ArrowUp } from "lucide-react";
import { fetchProducts, fetchCollections, ShopifyProduct, ShopifyCollection } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";
import { ShopMobileEditorial } from "@/components/shop/ShopMobileEditorial";
import { ShopCollectionSections } from "@/components/ShopCollectionSections";

import { SiteShell } from "@/components/SiteShell";
import { InlineBannerSlot } from "@/components/banners/BannerSlot";
import { PageHero } from "@/components/PageHero";

import { Skeleton } from "@/components/ui/skeleton";

import { useLocale } from "@/contexts/LocaleContext";
import { cn } from "@/lib/utils";
import {
  FAMILY_TAGS,
  MOOD_TAGS,
  getTag,
  type TagId,
} from "@/lib/scentTaxonomy";
import { countProductsPerTag, filterByTags } from "@/lib/smartSearch";
import { useProductTranslations } from "@/hooks/useProductTranslations";
import { notesByHandle } from "@/lib/shopify-localize";
import { useHeroVideoUrls } from "@/hooks/useHeroVideoUrls";
import { useGa4ViewItemList } from "@/hooks/useGa4ViewItemList";

import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import shopHeroFallback from "@/assets/collection-for-him-hero.webp";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/shop")({
  headers: publicCacheHeaders,
  head: () => ({
    meta: [
      { title: "The Collection — Lecmo" },
      {
        name: "description",
        content:
          "Browse the full Lecmo fragrance collection. Filter by family, mood, and occasion.",
      },
      { property: "og:title", content: "The Collection — Lecmo" },
      {
        property: "og:description",
        content: "Browse all fragrances composed by Lecmo.",
      },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/shop` },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/shop` },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Lecmo", item: `${PUBLIC_SITE_ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "Shop", item: `${PUBLIC_SITE_ORIGIN}/shop` },
          ],
        }),
      },
    ],
  }),
  component: ShopPage,
});

function ShopPage() {
  const { t, effectiveCountryCode } = useLocale();
  const heroVideo = useHeroVideoUrls("shop");

  const countryCode = effectiveCountryCode;
  const effectiveHeroImage = heroVideo.poster || shopHeroFallback;
  const [activeFamilies, setActiveFamilies] = React.useState<TagId[]>([]);
  const [activeMoods, setActiveMoods] = React.useState<TagId[]>([]);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const hasFilters = activeFamilies.length > 0 || activeMoods.length > 0;
  const { data, isLoading } = useQuery({
    queryKey: ["products", "all", countryCode],
    queryFn: () => fetchProducts(250, countryCode),
    placeholderData: keepPreviousData,
  });

  const { data: collections } = useQuery({
    queryKey: ["collections", "shop-strip"],
    queryFn: () => fetchCollections(50),
    staleTime: 5 * 60 * 1000,
  });

  // الفلترة الآن مركزيّة في `fetchProducts` عبر `filterVisibleProducts`
  // (راجع `HIDDEN_PRODUCT_TAGS` في src/lib/shopify.ts).
  const products: ShopifyProduct[] = data ?? [];

  useGa4ViewItemList({ listId: "shop", listName: "Shop", products });



  const translations = useProductTranslations();
  const notesMap = React.useMemo(() => notesByHandle(translations), [translations]);

  const counts = React.useMemo(() => countProductsPerTag(products, notesMap), [products, notesMap]);

  const filtered = React.useMemo(() => {
    const selected = [...activeFamilies, ...activeMoods];
    if (!selected.length) return products;
    return filterByTags(products, { anyTags: selected, notes: notesMap });
  }, [products, activeFamilies, activeMoods, notesMap]);

  const toggle = (
    list: TagId[],
    setList: (next: TagId[]) => void,
    id: TagId,
  ) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  return (
    <SiteShell>
      <PageHero
        pageKey="shop"
        image={effectiveHeroImage}
        videoUrl={heroVideo.desktop}
        mobileVideoUrl={heroVideo.mobile}
        campaignId={heroVideo.campaignId}
        title={t("shop.title")}
        subtitle={t("shop.subtitle")}
        alt={t("shop.title")}
      />


      <div className="mx-auto w-full max-w-6xl px-4 pt-6 sm:px-6">

        <InlineBannerSlot placement="shop_promo" />
      </div>



      <section className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 md:py-12">
        {/* العنوان العامّ يظهر فقط عند تفعيل الفلاتر — وإلّا تظهر أقسام Him/Her/Gifts بفواصل */}
        {hasFilters && (
          <div className="mb-4 md:mb-6">
            <h2 className="t-h2">{t("shop.section.all")}</h2>
          </div>
        )}
        <div className="mb-4 flex items-center justify-between gap-4 border-b border-border/60 pb-2 md:mb-8 md:pb-4">
          <span className="t-caption text-muted-foreground">
            {t("shop.filter.results", { count: String(filtered.length) })}
          </span>
          <div className="flex items-center gap-3">
            {hasFilters && (
              <button
                onClick={() => {
                  setActiveFamilies([]);
                  setActiveMoods([]);
                }}
                className="t-caption uppercase tracking-[var(--tracking-wider)] text-muted-foreground hover:text-primary"
              >
                {t("shop.filter.clear")}
              </button>
            )}
            <button
              onClick={() => setFilterOpen((v) => !v)}
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-4 py-2 t-caption transition-colors",
                hasFilters || filterOpen
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-foreground hover:border-primary hover:text-primary",
              )}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span>
                {hasFilters
                  ? t("shop.filter.open_with_count", {
                      count: String(activeFamilies.length + activeMoods.length),
                    })
                  : t("shop.filter.open")}
              </span>
            </button>
          </div>
        </div>

        {filterOpen && (
          <>
            <button
              type="button"
              aria-label={t("shop.filter.title")}
              onClick={() => setFilterOpen(false)}
              className="fixed inset-0 z-40 cursor-default bg-transparent"
            />
            <aside
              className="fixed inset-y-0 end-0 z-50 flex w-1/2 max-w-[280px] flex-col border-s border-border bg-background  animate-in slide-in-from-right duration-300"
              role="dialog"
              aria-modal="false"
            >
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <p className="t-eyebrow tracking-[var(--tracking-wider)]">
                  {t("shop.filter.title")}
                </p>
                <button
                  onClick={() => setFilterOpen(false)}
                  className="rounded-full p-1 text-muted-foreground hover:text-primary"
                  aria-label={t("shop.filter.clear")}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 space-y-8 overflow-y-auto px-5 py-6">
                <FilterRow
                  label={t("shop.filter.family")}
                  tags={FAMILY_TAGS}
                  active={activeFamilies}
                  counts={counts}
                  onToggle={(id) => toggle(activeFamilies, setActiveFamilies, id)}
                  tKey={t}
                />
                <FilterRow
                  label={t("shop.filter.mood")}
                  tags={MOOD_TAGS}
                  active={activeMoods}
                  counts={counts}
                  onToggle={(id) => toggle(activeMoods, setActiveMoods, id)}
                  tKey={t}
                />
              </div>
              <div className="flex items-center gap-3 border-t border-border bg-background px-5 py-4">
                {hasFilters && (
                  <button
                    onClick={() => {
                      setActiveFamilies([]);
                      setActiveMoods([]);
                    }}
                    className="inline-flex items-center gap-1 t-caption uppercase tracking-[var(--tracking-wider)] text-muted-foreground hover:text-primary"
                  >
                    <X className="h-3.5 w-3.5" />
                    {t("shop.filter.clear")}
                  </button>
                )}
                <button
                  onClick={() => setFilterOpen(false)}
                  className="ms-auto rounded-full bg-primary px-4 py-2 t-caption text-primary-foreground hover:bg-primary/90"
                >
                  {t("shop.filter.apply", { count: String(filtered.length) })}
                </button>
              </div>
            </aside>
          </>
        )}


        {/* عند تفعيل فلاتر العائلة/المزاج نعرض نتائج الفلترة داخل الحاوية */}
        {hasFilters &&
          (isLoading ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-3 md:gap-x-6 md:gap-y-12 lg:grid-cols-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="aspect-[3/4] w-full" />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <SegmentedProductGrid products={filtered} t={t} hasFilters={hasFilters} />
          ) : (
            <div className="rounded border border-dashed border-border py-24 text-center">
              <p className="t-h2">{t("shop.empty")}</p>
              <p className="mt-2 t-body-sm text-muted-foreground">{t("shop.empty_sub")}</p>
            </div>
          ))}
      </section>

      {/* بلا فلاتر: كلّ مجموعة تحت الأخرى بفاصل باسمها — edge-to-edge على خلفيّة بيضاء */}
      {!hasFilters && (
        <ShopCollectionSections
          collections={collections ?? []}
          products={products}
          isLoading={isLoading}
        />
      )}


    </SiteShell>
  );
}

function FilterRow({
  label,
  tags,
  active,
  counts,
  onToggle,
  tKey,
}: {
  label: string;
  tags: TagId[];
  active: TagId[];
  counts: Record<TagId, number>;
  onToggle: (id: TagId) => void;
  tKey: (k: string, vars?: Record<string, string>) => string;
}) {
  return (
    <div>
      <p className="mb-2 t-eyebrow-muted tracking-[var(--tracking-wider)]">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {tags.map((id) => {
          const tag = getTag(id)!;
          const isActive = active.includes(id);
          const count = counts[id] ?? 0;
          return (
            <button
              key={id}
              onClick={() => onToggle(id)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 t-caption transition-colors",
                isActive
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-foreground hover:border-primary hover:text-primary",
                count === 0 && !isActive && "opacity-40",
              )}
              disabled={count === 0 && !isActive}
            >
              <span>{tag.emoji}</span>
              <span>{tKey(`tag.${id}`)}</span>
              {count > 0 && (
                <span className="t-micro text-muted-foreground/80">({count})</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── تصنيف المنتج إلى قسم: gifts / him / her ───────────────────────
// أولويّة: tags صريحة في Shopify (for-him / for-her / gift / set / collection)
// ← ثمّ كلمات في العنوان (Collection/Set/perfume collection) ← Gift Boxes
// ← ثمّ heuristic بـ productType للجنس (مؤقّت لحين إضافة tags في Shopify)
type ShopSegment = "him" | "her" | "unisex" | "gifts";

function classifyProduct(p: ShopifyProduct): ShopSegment {
  const tags = (p.node.tags ?? []).map((x) => x.toLowerCase().trim());
  const title = p.node.title.toLowerCase();
  const type = (p.node.productType ?? "").toLowerCase();

  if (tags.some((x) => ["unisex", "for-unisex", "unisex-perfume"].includes(x))) return "unisex";
  if (tags.some((x) => ["for-him", "him", "men", "male", "homme"].includes(x))) return "him";
  if (tags.some((x) => ["for-her", "her", "women", "female", "femme"].includes(x))) return "her";
  if (
    tags.some((x) => ["gift", "gifts", "gift-box", "set", "collection"].includes(x)) ||
    /\b(collection|set)\b/.test(title) ||
    /gift|set|collection/.test(type)
  ) {
    return "gifts";
  }
  // Heuristic مبدئيّ بناءً على العائلة العطرية — قابل للتعديل من Shopify بإضافة tag
  if (/oriental|leather|woody|spicy|amber|oud/.test(type)) return "him";
  return "her"; // الباقي (Floral/Fresh/Chypre/Musky…) → For Her
}

function SegmentedProductGrid({
  products,
  t,
  hasFilters,
}: {
  products: ShopifyProduct[];
  t: (k: string, vars?: Record<string, string>) => string;
  hasFilters: boolean;
}) {
  const groups = React.useMemo(() => {
    const buckets: Record<ShopSegment, ShopifyProduct[]> = { him: [], her: [], unisex: [], gifts: [] };
    for (const p of products) buckets[classifyProduct(p)].push(p);
    return buckets;
  }, [products]);

  // عند تطبيق فلاتر العائلة/المزاج، نُخفي العناوين ونعرض شبكة واحدة لتفادي التشويش
  if (hasFilters) {
    return (
      <>
        <div className="md:hidden">
          <ShopMobileEditorial products={products} eager />
        </div>
        <div className="hidden md:grid md:grid-cols-3 md:gap-x-6 md:gap-y-12 lg:grid-cols-4">
          {products.map((p, i) => (
            <ProductCard key={p.node.id} product={p} priority={i < 4} lcp={i === 0} />
          ))}
        </div>
      </>
    );
  }



  const order: ShopSegment[] = ["him", "her", "unisex", "gifts"];
  const labelKey: Record<ShopSegment, string> = {
    him: "shop.section.him",
    her: "shop.section.her",
    unisex: "shop.section.unisex",
    gifts: "shop.section.gifts",
  };


  return (
    <div className="space-y-16">
      {order.map((seg, idx) => {
        const list = groups[seg];
        if (list.length === 0) return null;
        return (
          <div key={seg}>
            {/* فاصل ناعم بعنوان القسم */}
            <div className={cn("mb-8", idx > 0 && "pt-4")}>
              <div className="flex items-center gap-4">
                <span className="h-px flex-1 bg-border/60" />
                <h3 className="t-eyebrow tracking-[var(--tracking-wider)] uppercase text-foreground">
                  {t(labelKey[seg])}
                </h3>
                <span className="h-px flex-1 bg-border/60" />
              </div>
            </div>
            <div className="md:hidden">
              <ShopMobileEditorial products={list} eager={idx === 0} />
            </div>
            <div className="hidden md:grid md:grid-cols-3 md:gap-x-6 md:gap-y-12 lg:grid-cols-4">
              {list.map((p, i) => (
                <ProductCard key={p.node.id} product={p} priority={idx === 0 && i < 4} lcp={idx === 0 && i === 0} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CollectionsStrip({
  collections,
  label,
}: {
  collections: ShopifyCollection[];
  label: string;
}) {
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const [hasMore, setHasMore] = React.useState(false);
  const [isRtl, setIsRtl] = React.useState(false);
  // hide-on-scroll-down (موبايل فقط): يختفي عند النزول، يظهر فور الصعود
  const [hidden, setHidden] = React.useState(false);

  React.useEffect(() => {
    setIsRtl(document.documentElement.dir === "rtl");
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      // في RTL، scrollLeft سالب أو موجب حسب المتصفّح — نستخدم القيمة المطلقة
      const scrolled = Math.abs(el.scrollLeft);
      const max = el.scrollWidth - el.clientWidth;
      setHasMore(max > 4 && scrolled < max - 4);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [collections.length]);

  // حالة التمرير لأعلى: نمنع التكرار + نعرض حلقة تقدّم لحظيّة
  const scrollingUpRef = React.useRef(false);
  const [scrollingUp, setScrollingUp] = React.useState(false);
  const [progress, setProgress] = React.useState(0); // 0..1 (1 = وصلنا الأعلى)

  const handleScrollTop = React.useCallback(() => {
    if (scrollingUpRef.current) return; // منع النقرات المتكرّرة
    const startY = window.scrollY;
    if (startY <= 0) return; // أصلاً في الأعلى
    scrollingUpRef.current = true;
    setScrollingUp(true);
    setProgress(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
    const start = performance.now();
    const finish = () => {
      scrollingUpRef.current = false;
      setProgress(1);
      // إخفاء المؤشّر بعد لحظة قصيرة لإحساس الاكتمال
      window.setTimeout(() => setScrollingUp(false), 180);
    };
    const tick = () => {
      const y = window.scrollY;
      const p = Math.min(1, Math.max(0, 1 - y / startY));
      setProgress(p);
      if (y <= 4 || performance.now() - start > 1200) {
        finish();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, []);

  // مراقبة تمرير الصفحة لإخفاء/إظهار الشريط (موبايل فقط — sticky يُلغى md+)
  React.useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const SHOW_THRESHOLD = 80; // لا نخفيه إلا بعد تجاوز الهيرو القصير
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const dy = y - lastY;
        if (y < SHOW_THRESHOLD) {
          setHidden(false);
        } else if (dy > 4) {
          setHidden(true); // ينزل → اخفِ
        } else if (dy < -4) {
          setHidden(false); // يصعد → أظهر
        }
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <section
      className={cn(
        // sticky تحت الهيدر على الموبايل فقط؛ ديسكتوب = static كما كان
        "sticky top-16 z-30 border-b border-border/60 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 transition-transform duration-300 ease-out",
        "md:static md:top-auto md:z-auto md:bg-card md:backdrop-blur-none md:transform-none md:transition-none",
        hidden ? "-translate-y-full md:translate-y-0" : "translate-y-0",
      )}
    >
      <div className="mx-auto w-full max-w-6xl px-4 py-3 sm:px-6 md:py-5">
        <p className="t-eyebrow tracking-[var(--tracking-wider)] text-muted-foreground mb-2 md:mb-3">
          {label}
        </p>
        <div className="relative">
          <div
            ref={scrollerRef}
            className={cn(
              "flex items-center gap-x-5 overflow-x-auto whitespace-nowrap pb-1",
              // Snap لطيف لا يقطع الـmomentum على iOS ولا يحبس الإصبع على Android
              "snap-x snap-proximity scroll-px-6",
              // momentum على iOS Safari
              "[-webkit-overflow-scrolling:touch]",
              // عزل السحب عن الصفحة + توجيه الإيماءة الأفقيّة على Android
              "overscroll-x-contain touch-pan-x",
              // إخفاء scrollbar على كلّ المتصفّحات
              "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
              // تجربة لمس نظيفة (لا hilite أزرق ولا تحديد نصّ مزعج)
              "select-none [-webkit-tap-highlight-color:transparent]",
              // على الديسكتوب لا حاجة لـsnap
              "md:snap-none md:select-auto",
            )}
          >
            {collections.map((c) => (
              <Link
                key={c.handle}
                to="/collection/$handle"
                params={{ handle: c.handle }}
                draggable={false}
                className={cn(
                  "t-eyebrow tracking-[var(--tracking-wider)] uppercase text-foreground/80 hover:text-primary transition-colors shrink-0 snap-start [scroll-snap-stop:normal]",
                  // إعدادات لمس موحَّدة: نمرّر إيماءة الـscroll الأفقي للأبّ، ونمنع تحديد النصّ ووميض اللمس
                  "touch-pan-x select-none [-webkit-tap-highlight-color:transparent] [-webkit-user-drag:none]",
                  // على الديسكتوب: استرجاع التحديد الطبيعيّ
                  "md:select-auto md:touch-auto",
                )}
              >
                {c.title}
              </Link>
            ))}
            {/* مساحة على الحافّة كي لا يختفي آخر عنصر تحت الشيفرون */}
            <span aria-hidden className="shrink-0 w-8 md:hidden" />
          </div>

          {/* تدرّج خفيف على الحافّة الأماميّة (اتّجاه السحب) */}
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-y-0 w-12 transition-opacity duration-200 md:hidden",
              isRtl
                ? "start-0 bg-gradient-to-r from-card to-transparent"
                : "end-0 bg-gradient-to-l from-card to-transparent",
              hasMore ? "opacity-100" : "opacity-0",
            )}
          />

          {/* مؤشّر السحب: شيفرون نابض */}
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-y-0 flex items-center transition-opacity duration-200 md:hidden",
              isRtl ? "start-1" : "end-1",
              hasMore ? "opacity-100" : "opacity-0",
            )}
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 text-primary animate-pulse",
                isRtl && "rotate-180",
              )}
            />
          </div>
        </div>
      </div>
    </section>

    {/* زرّ عائم صغير: يظهر على الموبايل فقط حين يُخفى الشريط — يعيد للأعلى فيعود الشريط */}
    <button
      type="button"
      aria-label={label}
      onClick={handleScrollTop}
      disabled={scrollingUp}
      aria-disabled={scrollingUp}
      className={cn(
        "fixed bottom-24 end-4 z-30 md:hidden",
        "h-10 w-10 rounded-full bg-card/95 backdrop-blur border border-border/60 ",
        "flex items-center justify-center text-foreground/80 hover:text-primary",
        "transition-all duration-300 ease-out",
        "select-none touch-manipulation [-webkit-tap-highlight-color:transparent] [-webkit-user-drag:none]",
        hidden
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-2 pointer-events-none",
      )}
    >
      {/* حلقة تقدّم لحظيّة: تظهر فقط أثناء حركة العودة للأعلى */}
      <svg
        aria-hidden
        viewBox="0 0 36 36"
        className={cn(
          "absolute inset-0 h-full w-full -rotate-90 transition-opacity duration-200",
          scrollingUp ? "opacity-100" : "opacity-0",
        )}
      >
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          strokeWidth="2"
          className="stroke-primary"
          strokeLinecap="round"
          strokeDasharray={2 * Math.PI * 16}
          strokeDashoffset={2 * Math.PI * 16 * (1 - progress)}
          style={{ transition: "stroke-dashoffset 80ms linear" }}
        />
      </svg>
      <ArrowUp className="h-4 w-4 relative" aria-hidden />
    </button>
    </>
  );
}
